/**
 * Quando um módulo pode oferecer o atalho para o outro, na visão do cidadão.
 *
 * As telas principais do cidadão se apontam mutuamente: a da Ouvidoria tem o cartão
 * "Solicitação", que leva ao pedido do e-SIC, e a do e-SIC tem o "Acesse aqui", que leva à
 * Ouvidoria. Cada atalho depende de duas coisas:
 *
 * 1. **A entidade contratou o módulo de destino** (`clientApp.possuiEsic` / `possuiOuvidoria`).
 *    É a única condição das telas legadas — `@if(configView.sessao.possuiEsic)` em
 *    `principalOuvidoriaPubMDN` e `@if(configView.sessao.possuiOuvidoria)` em
 *    `principalEsicPubMDN`. Sem o módulo não há para onde levar.
 * 2. **A pessoa é cidadã cadastrada nesta entidade** — o direito `sistema/cidadao`, que vira
 *    `ROLE_CIDADAO` no token. É o que o cadastro concede (`RegistroCidadaoService`); sem ele a
 *    tela de destino responderia 403.
 *
 * <p>O que este papel <b>não</b> distingue: o cadastro de cidadão é um só por entidade,
 * compartilhado pelos dois módulos — `RegistroCidadaoService` concede o mesmo direito citando
 * BR-ESIC-074 e BR-OUV-036. Não existe "cadastro de cidadão do e-SIC" separado do "da Ouvidoria":
 * quem se cadastrou por um está apto no outro, desde que a entidade o tenha. O que a regra
 * efetivamente exclui é o servidor que só tem papéis de operador.</p>
 *
 * <p>Funções puras, e em `core/contexto` e não dentro de uma feature, porque cada uma decide numa
 * feature diferente (e o menu lateral do cidadão, montado no `AppComponent`) — e o lint proíbe
 * uma feature importar de outra.</p>
 */

/** Verdadeiro se a pessoa tem cadastro de cidadão na entidade corrente. */
function ehCidadao(papeis: readonly string[]): boolean {
  // As duas grafias, como o `AuthService.hasRole`: o token traz `ROLE_CIDADAO`, mas as telas
  // costumam escrever o papel sem o prefixo.
  return papeis.includes('ROLE_CIDADAO') || papeis.includes('CIDADAO');
}

/**
 * Verdadeiro se a pessoa tem ao menos um papel de administração do módulo — servidor da
 * Ouvidoria (admin, cadastrador ou operador) ou do e-SIC (gestor, cadastrador, respondente).
 *
 * <p>Por prefixo, e não por lista fixa: o backend gera `ROLE_<GRUPO>_<PAPEL>` para qualquer par
 * (`Papeis.generico`), então um papel novo do módulo passa a valer sem alterar esta regra.</p>
 */
function temPapelDeAdministracao(papeis: readonly string[], grupo: 'OUVIDORIA' | 'ESIC'): boolean {
  return papeis.some(papel => papel.startsWith(`ROLE_${grupo}_`) || papel.startsWith(`${grupo}_`));
}

/** O cidadão que está na Ouvidoria pode abrir um pedido no e-SIC da mesma entidade? */
export function podeAbrirPedidoNoEsic(possuiEsic: boolean, papeis: readonly string[]): boolean {
  return possuiEsic && ehCidadao(papeis);
}

/**
 * Quem está no e-SIC pode ir para a Ouvidoria da mesma entidade?
 *
 * <p>Além do cidadão, vale para o <b>servidor da Ouvidoria</b>: quem tem qualquer papel de
 * administração do módulo já o acessa, e esconder o atalho dele só o obrigaria a voltar pelo
 * login. O cadastro de cidadão e os papéis de servidor são coisas distintas — um servidor pode
 * não ter cadastro de cidadão na entidade.</p>
 */
export function podeAcessarOuvidoria(possuiOuvidoria: boolean, papeis: readonly string[]): boolean {
  return possuiOuvidoria && (ehCidadao(papeis) || temPapelDeAdministracao(papeis, 'OUVIDORIA'));
}
